import {
    useCallback,
    useMemo,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type ReactElement,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronRight, FolderOpen, Lock, Plus, Users } from "lucide-react";
import { color, space, useListItemMotion } from "@tokens/index";
import { Badge } from "@primitives/Badge";
import { ContentSwap, type ContentSwapSurface } from "@primitives/ContentSwap";
import { IconButton } from "@primitives/IconButton";
import { motionStyle } from "@primitives/motionStyle";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import type { QueueSummary, QueueTreeNode, QueueWorkItemTally } from "../../experience-shell/queues";
import { ListEmptyLine, ListError, ListSkeleton } from "./queue-surfaces/ListStates";
import { listRow, listStack } from "./queue-surfaces/listSurface";
import { TallyBadge } from "./TallyBadge";
import styles from "./QueueTree.module.css";

// ---------------------------------------------------------------------------
// Public types
//
// QueueTree renders an indented hierarchy of queues. Non-workable "category"
// nodes are headers (no member count, no open affordance). Workable queues
// are rows that select on activation. The owning page supplies tree data and
// intent callbacks.
// ---------------------------------------------------------------------------

export interface QueueTreeProps {
    readonly roots: readonly QueueTreeNode[];
    readonly isLoading: boolean;
    readonly error?: Error | null;
    readonly onSelect?: (queue: QueueSummary) => void;
    readonly onCreateChild?: (parent: QueueSummary) => void;
    /**
     * Per-queue Open/Pending tallies indexed by queue id. Missing entries
     * render as em dashes so the row does not jump during the initial fetch.
     */
    readonly tallies?: ReadonlyMap<string, QueueWorkItemTally>;
    /**
     * Invoked when the user activates an Open or Pending chip to drill into
     * the work items behind that count. When provided, the chips are buttons.
     */
    readonly onTallyClick?: (queue: QueueSummary, bucket: "open" | "pending") => void;
    /**
     * When set, personal queues (plain roots from the API) fold under a
     * synthetic, collapsed-by-default "Personal Queues" header pinned to the
     * bottom of the list. Presentation only; derived from `isPersonal` each
     * render.
     */
    readonly groupPersonalQueues?: boolean;
    /** Background of the host the tree sits on; see `ContentSwap`. Defaults to `canvas`. */
    readonly surface?: ContentSwapSurface | undefined;
}

// ---------------------------------------------------------------------------
// Synthetic "Personal Queues" group
//
// The id is a sentinel that cannot collide with a queue GUID, so the row
// renderer can special-case the header (no add-child affordance).
// ---------------------------------------------------------------------------

const PERSONAL_QUEUES_GROUP_ID = "__personal-queues-group__";

const PERSONAL_QUEUES_GROUP_SUMMARY: QueueSummary = {
    id: PERSONAL_QUEUES_GROUP_ID,
    name: "Personal Queues",
    isPersonal: false,
    ownerUserId: null,
    ownerManagementGroupId: null,
    createdAt: "",
    memberCount: 0,
    members: [],
    parentQueueId: null,
    isWorkable: false,
    depth: 0,
    ancestorPath: [],
    canAdminister: false,
    status: "active",
    deactivationStartedAt: null,
    deactivatedAt: null,
};

const DISCLOSURE_SLOT = 32;
const INDENT_PER_LEVEL = 20;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface FlatRow {
    readonly node: QueueTreeNode;
    readonly depth: number;
    readonly hasChildren: boolean;
    readonly isExpanded: boolean;
}

// Flatten the tree into a render list, gating descendants behind the
// expansion state, so keyboard navigation uses one linear index.
function flatten(roots: readonly QueueTreeNode[], expanded: ReadonlySet<string>): FlatRow[] {
    const out: FlatRow[] = [];

    function walk(node: QueueTreeNode, depth: number): void {
        const hasChildren = node.children.length > 0;
        const isExpanded = expanded.has(node.queue.id);
        out.push({ node, depth, hasChildren, isExpanded });
        if (hasChildren && isExpanded) {
            for (const child of node.children) {
                walk(child, depth + 1);
            }
        }
    }

    for (const root of roots) {
        walk(root, 0);
    }
    return out;
}

function plural(count: number, noun: string): string {
    return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QueueTree({
    roots,
    isLoading,
    error,
    onSelect,
    onCreateChild,
    tallies,
    onTallyClick,
    groupPersonalQueues,
    surface,
}: QueueTreeProps): ReactElement {
    const { isCompact } = useViewport();

    const effectiveRoots = useMemo<readonly QueueTreeNode[]>(() => {
        if (groupPersonalQueues !== true) return roots;
        const personal = roots.filter((r) => r.queue.isPersonal);
        if (personal.length === 0) return roots;
        const shared = roots.filter((r) => !r.queue.isPersonal);
        return [...shared, { queue: PERSONAL_QUEUES_GROUP_SUMMARY, children: personal }];
    }, [roots, groupPersonalQueues]);

    // Every group starts collapsed so the index reads as a short list of
    // categories. Expansion is local user intent.
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());

    const rows = useMemo(() => flatten(effectiveRoots, expanded), [effectiveRoots, expanded]);

    const toggle = useCallback((id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleRowKey = useCallback(
        (e: KeyboardEvent<HTMLDivElement>, row: FlatRow) => {
            const queue = row.node.queue;
            if (e.key === "Enter" || e.key === " ") {
                if (queue.isWorkable && onSelect !== undefined) {
                    e.preventDefault();
                    onSelect(queue);
                }
            } else if (e.key === "ArrowRight" && row.hasChildren && !row.isExpanded) {
                e.preventDefault();
                toggle(queue.id);
            } else if (e.key === "ArrowLeft" && row.hasChildren && row.isExpanded) {
                e.preventDefault();
                toggle(queue.id);
            }
        },
        [onSelect, toggle],
    );

    if (error !== null && error !== undefined) {
        return <ListError>Couldn&rsquo;t load queues. {error.message}</ListError>;
    }

    // Rows on screen when the tree fills animate nothing; rows revealed by a
    // later expand or a refetch fade in.
    return (
        <ContentSwap
            ready={!isLoading || rows.length > 0}
            surface={surface}
            skeleton={<ListSkeleton label="Loading queues" count={4} leading={16} trailing={48} />}
        >
            {rows.length === 0 ? (
                <ListEmptyLine>No queues yet.</ListEmptyLine>
            ) : (
                <div role="tree" aria-label="Queue hierarchy" style={listStack}>
                    <AnimatePresence initial={false}>
                        {rows.map((row, index) => {
                            const queue = row.node.queue;
                            const rowTally = tallies?.get(queue.id);
                            return (
                                <QueueTreeRow
                                    key={queue.id}
                                    row={row}
                                    index={index}
                                    compact={isCompact}
                                    onToggle={() => toggle(queue.id)}
                                    onKey={handleRowKey}
                                    {...(rowTally !== undefined ? { tally: rowTally } : {})}
                                    {...(onTallyClick !== undefined ? { onTallyClick } : {})}
                                    {...(onSelect !== undefined ? { onSelect } : {})}
                                    {...(onCreateChild !== undefined ? { onCreateChild } : {})}
                                />
                            );
                        })}
                    </AnimatePresence>
                </div>
            )}
        </ContentSwap>
    );
}

// ---------------------------------------------------------------------------
// Tally chips
// ---------------------------------------------------------------------------

function PendingCount({ label }: { readonly label: string }): ReactElement {
    return (
        <span role="img" aria-label={label} title={label}>
            <Text role="caption" tone="subtle" tabular>
                {"\u2014"}
            </Text>
        </span>
    );
}

// An Open or Pending chip. An undefined count (tally fetch in flight) renders
// a muted em dash so a non-loaded state cannot read as zero.
function renderTallyChip(
    bucket: "open" | "pending",
    count: number | undefined,
    onActivate: (() => void) | undefined,
    activateLabel: string,
): ReactElement {
    if (count === undefined) {
        const label = bucket === "open" ? "Open work items" : "Pending work items";
        return <PendingCount label={`${label} loading`} />;
    }
    return (
        <TallyBadge
            bucket={bucket}
            count={count}
            {...(onActivate !== undefined ? { onActivate, activateLabel } : {})}
        />
    );
}

// ---------------------------------------------------------------------------
// Row sub-component
// ---------------------------------------------------------------------------

interface RowProps {
    readonly row: FlatRow;
    readonly index: number;
    readonly compact: boolean;
    readonly onToggle: () => void;
    readonly onSelect?: (queue: QueueSummary) => void;
    readonly onCreateChild?: (parent: QueueSummary) => void;
    readonly onKey: (e: KeyboardEvent<HTMLDivElement>, row: FlatRow) => void;
    /** Tally for this queue. Absent while the fetch is in flight. */
    readonly tally?: QueueWorkItemTally;
    readonly onTallyClick?: (queue: QueueSummary, bucket: "open" | "pending") => void;
}

function QueueTreeRow({
    row,
    index,
    compact,
    onToggle,
    onSelect,
    onCreateChild,
    onKey,
    tally,
    onTallyClick,
}: RowProps): ReactElement {
    const arrival = useListItemMotion(index, false);
    const queue = row.node.queue;
    const isCategory = !queue.isWorkable;
    const isActive = queue.status === "active";
    const selectable = queue.isWorkable && onSelect !== undefined;

    // Children of root queues are allowed (depth 0 -> 1). The two-level cap
    // suppresses the affordance deeper down. The synthetic Personal Queues
    // header is never a real parent.
    const canAddChild =
        onCreateChild !== undefined &&
        isActive &&
        queue.depth === 0 &&
        queue.id !== PERSONAL_QUEUES_GROUP_ID;

    const membersLabel = plural(queue.memberCount, "member");
    const breakdown =
        tally !== undefined
            ? `${membersLabel} · ${String(tally.open)} open · ${String(tally.pending)} pending`
            : membersLabel;

    const style: CSSProperties = {
        ...listRow("transparent", { hovered: false, selected: false }),
        background: undefined,
        gap: space[2],
        paddingInlineStart: `calc(${space[1]} + ${String(row.depth * INDENT_PER_LEVEL)}px)`,
        paddingInlineEnd: space[2],
        cursor: selectable ? "pointer" : "default",
        opacity: isActive ? 1 : 0.56,
    };

    return (
        <motion.div
            role="treeitem"
            tabIndex={selectable ? 0 : -1}
            aria-level={row.depth + 1}
            aria-expanded={row.hasChildren ? row.isExpanded : undefined}
            className={styles.row}
            data-selectable={selectable || undefined}
            {...(selectable ? { onClick: () => onSelect(queue) } : {})}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => onKey(e, row)}
            {...(!isCategory && compact ? { title: breakdown } : {})}
            style={motionStyle(style)}
            initial={arrival.initial}
            animate={arrival.animate}
            transition={arrival.transition}
        >
            {row.hasChildren ? (
                <IconButton
                    icon={row.isExpanded ? ChevronDown : ChevronRight}
                    size="sm"
                    variant="ghost"
                    aria-label={row.isExpanded ? "Collapse" : "Expand"}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                    }}
                />
            ) : (
                <span
                    style={{ width: DISCLOSURE_SLOT, height: DISCLOSURE_SLOT, flexShrink: 0 }}
                    aria-hidden="true"
                />
            )}

            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isCategory ? color.fg.subtle : color.accent.primary,
                    flexShrink: 0,
                }}
                aria-hidden="true"
            >
                {isCategory ? (
                    <FolderOpen size={14} strokeWidth={1.75} />
                ) : queue.isPersonal ? (
                    <Lock size={12} strokeWidth={2} />
                ) : (
                    <Users size={14} strokeWidth={1.75} />
                )}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: space[2], flex: 1, minWidth: 0 }}>
                <Text
                    role={isCategory ? "eyebrow" : "bodySm"}
                    tone={isCategory ? "subtle" : "default"}
                    weight={isCategory ? "semibold" : "medium"}
                    truncate
                >
                    {queue.name}
                </Text>
                {!isActive && (
                    <Badge
                        variant={queue.status === "deactivating" ? "warning" : "neutral"}
                        size="sm"
                    >
                        {queue.status === "deactivating" ? "Deactivating" : "Deactivated"}
                    </Badge>
                )}
            </div>

            {!isCategory && (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: space[2],
                        flexShrink: 0,
                    }}
                >
                    {compact ? (
                        isActive && (
                            tally !== undefined ? (
                                <TallyBadge bucket="total" count={tally.open + tally.pending} />
                            ) : (
                                <PendingCount label="Work items loading" />
                            )
                        )
                    ) : (
                        <>
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: space[1],
                                    color: color.fg.muted,
                                }}
                                aria-label={membersLabel}
                                title={membersLabel}
                            >
                                <Users size={12} strokeWidth={1.75} aria-hidden="true" />
                                <Text role="caption" tone="subtle" tabular>
                                    {queue.memberCount}
                                </Text>
                            </span>
                            {isActive &&
                                renderTallyChip(
                                    "open",
                                    tally?.open,
                                    onTallyClick !== undefined
                                        ? () => onTallyClick(queue, "open")
                                        : undefined,
                                    `View ${plural(tally?.open ?? 0, "open work item")} in ${queue.name}`,
                                )}
                            {isActive &&
                                renderTallyChip(
                                    "pending",
                                    tally?.pending,
                                    onTallyClick !== undefined
                                        ? () => onTallyClick(queue, "pending")
                                        : undefined,
                                    `View ${plural(tally?.pending ?? 0, "pending work item")} in ${queue.name}`,
                                )}
                        </>
                    )}
                </span>
            )}

            {canAddChild && (
                <IconButton
                    icon={Plus}
                    size="sm"
                    variant="ghost"
                    aria-label={`Add a queue under ${queue.name}`}
                    tooltip="Add child queue"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCreateChild?.(queue);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                    }}
                />
            )}
        </motion.div>
    );
}
